// Components/Common/usePendingApprovals.js
//
// Polls /api/pending-approvals/summary every 60s and exposes the aggregated
// "things awaiting my action" counts. Shared by the status-bar banner and the
// notification bell so both stay in sync from one request.

import { useEffect, useState, useCallback, useRef } from "react";
import api from "../../api";

const POLL_MS = 60000;

export default function usePendingApprovals() {
    const [summary, setSummary] = useState({ total: 0, totalPending: 0, areas: [] });
    const [loading, setLoading] = useState(true);
    const timer = useRef(null);
    const summaryRef = useRef(summary);
    summaryRef.current = summary;

    const fetchSummary = useCallback(async () => {
        try {
            const { data } = await api.get("pending-approvals/summary");
            setSummary({
                total: data?.total ?? 0,
                totalPending: data?.totalPending ?? 0,
                areas: Array.isArray(data?.areas) ? data.areas : [],
            });
        } catch {
            // Leave the previous value on transient errors — don't flash to zero.
        } finally {
            setLoading(false);
        }
    }, []);

    // Bank the "last seen" cursor for an area so its NEW count drops to 0.
    // Called when the user clicks through to (or dismisses) an area.
    const markSeen = useCallback(async (area, cursor) => {
        try {
            await api.post("pending-approvals/seen", { area, cursor });
            // Optimistically zero this area's NEW count locally (the workload
            // Total stays — it only drops when the item is actually actioned).
            setSummary((prev) => {
                const areas = (prev.areas || []).map((a) =>
                    a.key === area ? { ...a, count: 0 } : a);
                return {
                    total: areas.reduce((s, a) => s + (a.count || 0), 0),
                    totalPending: prev.totalPending,
                    areas,
                };
            });
        } catch { /* best-effort */ }
    }, []);

    // Mark EVERY area seen at once — used when the user opens the bell, so the
    // "new" badge clears like a normal notification bell. Totals are untouched.
    const markAllSeen = useCallback(async () => {
        const areas = summaryRef.current?.areas || [];
        const toBank = areas.filter((a) => (a.count || 0) > 0);
        if (toBank.length === 0) return;
        // Optimistically zero all new counts now.
        setSummary((prev) => ({
            total: 0,
            totalPending: prev.totalPending,
            areas: (prev.areas || []).map((a) => ({ ...a, count: 0 })),
        }));
        // Persist each cursor (best-effort, parallel).
        await Promise.allSettled(
            toBank.map((a) => api.post("pending-approvals/seen", { area: a.key, cursor: a.cursor }))
        );
    }, []);

    useEffect(() => {
        fetchSummary();
        timer.current = setInterval(fetchSummary, POLL_MS);
        return () => { if (timer.current) clearInterval(timer.current); };
    }, [fetchSummary]);

    return { summary, loading, refresh: fetchSummary, markSeen, markAllSeen };
}
